/**
 * # KPI
 * Key Performance Indicator
 *
 * @example
 * new KPI(el, {
 *    // ...
 *  }
 * });
 *
 * @module components/kpi.js
 */

import BaseComponent from './base';
import getParent from '../helpers/traversal/get-parent';
import addClass from '../helpers/dom/add-class';
import removeClass from '../helpers/dom/remove-class';
import debounce from '../helpers/util/debounce';
import hasClass from '../helpers/dom/has-class';
import { get as getBreakpoint } from '../helpers/dom/breakpoint';
import FixedPanel from './fixed-panel';
import PanelCarousel from './panel-carousel';


class KPI extends BaseComponent {

  /**
   * KPI constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    super(el, params);

    if (!el) {
      return;
    }

    if (hasClass(this.el, 'spark-kpi--tabs')) {
      this._hasTabs = true;
    }

    this._bindEventListenerCallbacks();
    this._addEventListeners();
    this._calcBreakpoint();
    this._updateOrientation();
    this._initFixedPanel(el, params);
    this._initPanelCarousel(el, params);
  }


  /**
   * If using a locked panel create a Fixed Panel Component and pass along config options
   */
  _initFixedPanel() {
    if (this.lockPanel === true) {
      addClass(this.el, 'spark-panel--fixed');
      addClass(this.el, 'spark-panel--shadow-0');
      addClass(this.el, 'spark-panel--border-0');
      this.fixedPanel = new FixedPanel(this.el, {
        condenseOnScroll: this.condenseOnScroll,
        unlockBreakPoint: this.stackBreakPoint,
        fixToBottom: this.fixToBottom,
        customCondenseClass: this.customCondenseClass,
        scrolledToEl: this.scrolledToEl,
        hideOnScroll: this.hideOnScroll || false,
        headerEl: this.headerEl
      });
    }
  }


  /**
   * Add Panel Carousel Functionality pass parameters to the them
   */
  _initPanelCarousel() {
    if (!hasClass(this.el, '.spark-panel-carousel')) {
      addClass(this.el, 'spark-panel-carousel');
    }

    if (!hasClass(this.el.querySelector('.spark-kpi__list'), 'spark-panel-carousel__list')) {
      addClass(this.el.querySelector('.spark-kpi__list'), 'spark-panel-carousel__list');
    }

    if (!hasClass(this.el.querySelector('.spark-kpi__item'), 'spark-panel-carousel__item')) {
      const items = this.el.querySelectorAll('.spark-kpi__item');
      for (let i = 0; i < items.length; i++) {
        addClass(items[i], 'spark-panel-carousel__item');
      }
    }

    this.panelCarousel = new PanelCarousel(this.el, {
      noCarouselBreakPoint: this.stackBreakPoint,
      singleCarouselAdvance: this.singleCarouselAdvance,
      controlBreakPoint: this.controlBreakPoint,
      nextLabel: this.nextLabel,
      previousLabel: this.previousLabel,
      hasTabs: this._hasTabs
    });
  }


  /**
   * Add event listeners
   */
  _addEventListeners() {
    window.addEventListener('resize', this._onResizeBound);
    if (this._hasTabs === true) {
      this._kpiList.addEventListener('click', this._onTabListClickBound);
    }
  }


  /**
   * Remove components
   */
  remove() {
    this.panelCarousel.remove();
    if (this.lockPanel === true) {
      this.lockPanel.remove();
    }
    super.remove();
  }


  /**
   * Remove event listeners
   */
  _removeEventListeners() {
    window.removeEventListener('resize', this._onResizeBound);
    if (this._hasTabs === true) {
      this._kpiList.removeEventListener('click', this._onTabListClickBound);
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onResizeBound = debounce(this._onResize.bind(this), 90);
    this._onTabListClickBound = this._onTabListClick.bind(this);
  }


  /**
   * Store a reference to the tabs list, each tab and each panel.
   * Set which tab is active, or use the first.
   * @param {Element} el
   */
  _cacheElements(el) {
    this.el = el;
    this._currentTab = this.el.querySelector('[aria-selected="true"]');
    this._kpiList = this.el.querySelector('.spark-kpi__list');
    this._kpiItems = this.el.querySelectorAll('.spark-kpi__item');
  }


  /**
   * Window resize event, keeps the carousel fresh... debounced from the listener. an event is passed to the method, but not used.
   */
  _onResize() {
    this._calcBreakpoint();
    this._updateOrientation();
  }


  /**
   * Calculate the current Break point, update flags based on value
   */
  _calcBreakpoint() {
    this.currentBreakpoint = getBreakpoint(window.innerWidth);
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
    const currentBreakIndex = breakpoints.indexOf(this.currentBreakpoint);
    const stackBreakIndex = breakpoints.indexOf(this.stackBreakPoint);

    if (currentBreakIndex <= stackBreakIndex && stackBreakIndex !== -1) {
      this._useStackedKPI = true;
    } else {
      this._useStackedKPI = false;
    }
  }


  /**
   * Update the orientation, row or stacked KPI
   */
  _updateOrientation() {
    if (this._useStackedKPI === true) {
      if (!hasClass(this.el, 'spark-kpi--column')) {
        addClass(this.el, 'spark-kpi--column');
        this.el.removeAttribute('data-overflow');
      }
    } else {
      removeClass(this.el, 'spark-kpi--column');
    }
  }


  /**
   * Tab KPI list was clicked.
   * @param  {clickEvent} _event click
   */
  _onTabListClick(_event) {
    _event.preventDefault();
    let tab = _event.target;

    if (tab.nodeName !== 'A') {
      tab = getParent(tab, 'a', this.el);
    }
    if (tab === false) {
      return;
    }

    if (this._currentTab !== null) {
      this._currentTab.removeAttribute('aria-selected');
    }

    tab.setAttribute('aria-selected', 'true');
    this._currentTab = tab;

    if (typeof this.onTabClick === 'function') {
      this.onTabClick(tab.getAttribute('aria-controls'), tab, this);
    }
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
KPI.prototype._whitelistedParams = [
  'singleCarouselAdvance',
  'lockPanel',
  'condenseOnScroll',
  'customCondenseClass',
  'unlockBreakPoint',
  'stackBreakPoint',
  'controlBreakPoint',
  'fixToBottom',
  'hideOnScroll',
  'onTabClick',
  'scrolledToEl',
  'headerEl',
];


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
KPI.prototype.defaults = {
  el: null,
  singleCarouselAdvance: false,
  lockPanel: false,
  condenseOnScroll: true,
  customCondenseClass: 'spark-kpi--condensed',
  unlockBreakPoint: null,
  stackBreakPoint: 'xs',
  controlBreakPoint: 'xs',
  fixToBottom: null,
  hideOnScroll: false,
  onTabClick: null,
  nextLabel: 'show next',
  previousLabel: 'show previous',
  scrolledToEl: null,
  headerEl: null,
  _useControls: true,
  _useStackedKPI: false,
  _currentTab: null,
  _kpiItemsWidth: 0,
  _kpiWidth: 0,
  _kpiChange: 0,
  _positionX: 0,
  _forwardLimit: false,
  _backwardLimit: true,
  _overflow: false,
  _hasTabs: false,
  fixedPanel: null
};

export default KPI;
